import { TableCell, TableRow } from '@mui/material';
import { FC, Fragment, Reducer, useCallback, useEffect, useReducer } from 'react';
// components
import TableLoader from 'components/common/TableLoader';
import NoDataFound from 'components/common/NoDataFound';
import TableContainer from 'components/common/TableContainer';
import TableComponent from 'components/common/TableComponent';
import TableSearchField from 'components/common/TableSearchField';
import ActionMenuDropdown from 'components/common/ActionMenuDropdown';
// constants, reducers, graphql, styles
import {
  HTTP_STATUS,
  LOADING_TABLE_ROWS,
  EDIT_DV_CONSTRAINTS_ROUTE,
  DV_CONSTRAINTS_TABLE_HEADER,
} from 'constants/index';
import { useDebounce } from 'hooks/useDebounce';
import { textWhiteSpace } from 'styles/commonComponentStyle';
import { useFindAllDvConstraintsTableLazyQuery, DailyValueConstraintsPayload } from 'generated/graphql';
import { Action, ActionType, State, initialState, dvConstraintsReducer } from 'reducer/dvConstraintsReducer';

const DvConstraintTable: FC = () => {
  const [state, dispatch] = useReducer<Reducer<State, Action>>(dvConstraintsReducer, initialState);
  const { page, rowsPerPage, search, count, data } = state;

  const debouncedSearch = useDebounce(search);

  const [findAllDvConstraints, { loading, error }] = useFindAllDvConstraintsTableLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { findAllDailyValueConstraints } = data;
      const { pagination, response, data: dvConstraintsData } = findAllDailyValueConstraints || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        const { page, totalCount } = pagination || {};
        dispatch({ type: ActionType.SET_PAGE, page: page || 1 });
        dispatch({ type: ActionType.SET_COUNT, count: totalCount || 0 });
        dispatch({
          type: ActionType.SET_DATA,
          data: dvConstraintsData as DailyValueConstraintsPayload['data'],
        });
      } else {
        resetPage();
      }
    },

    onError: () => {
      resetPage();
    },
  });

  const resetPage = () => {
    dispatch({ type: ActionType.SET_PAGE, page: 1 });
    dispatch({ type: ActionType.SET_COUNT, count: 0 });
    dispatch({ type: ActionType.SET_DATA, data: [] });
  };

  const fetchCapsuleTypes = useCallback(async () => {
    await findAllDvConstraints({
      variables: {
        findAllDailyValueConstraintsInput: {
          paginationOptions: {
            limit: rowsPerPage,
            page,
          },
          search: debouncedSearch,
        },
      },
    });
  }, [findAllDvConstraints, debouncedSearch, rowsPerPage, page]);

  useEffect(() => {
    fetchCapsuleTypes();
  }, [fetchCapsuleTypes]);

  const noData = Boolean((!loading && data?.length === 0) || error);

  return (
    <TableContainer>
      <TableSearchField
        search={search}
        onClear={() => dispatch({ type: ActionType.SET_SEARCH, search: '' })}
        onChange={(val) => dispatch({ type: ActionType.SET_SEARCH, search: val })}
      />

      <TableComponent
        noData={noData}
        page={page - 1}
        count={count}
        rowsPerPage={rowsPerPage}
        tableHeader={DV_CONSTRAINTS_TABLE_HEADER}
        setPage={(p: number) => dispatch({ type: ActionType.SET_PAGE, page: p + 1 })}
        setRowsPerPage={(r: number) => dispatch({ type: ActionType.SET_ROWS_PER_PAGE, rowsPerPage: r })}>
        {loading ? (
          <TableLoader columns={3} rows={LOADING_TABLE_ROWS} />
        ) : (
          <Fragment>
            {data?.map((cell) => {
              const {
                id,
                name,
                unit,
                infants,
                foodComponent,
                adultsAndChildren,
                children1To3Years,
                pregnantAndLactatingWomen,
              } = cell || {};

              return (
                <TableRow key={id}>
                  <TableCell sx={textWhiteSpace}>{name ?? '--'}</TableCell>
                  <TableCell>{foodComponent ?? '--'}</TableCell>
                  <TableCell>{infants ?? '--'}</TableCell>
                  <TableCell>{adultsAndChildren ?? '--'}</TableCell>
                  <TableCell>{children1To3Years ?? '--'}</TableCell>
                  <TableCell>{pregnantAndLactatingWomen ?? '--'}</TableCell>
                  <TableCell>{unit ?? '--'}</TableCell>

                  <TableCell>
                    <ActionMenuDropdown id={id ?? ''} editRoute={EDIT_DV_CONSTRAINTS_ROUTE} />
                  </TableCell>
                </TableRow>
              );
            })}
          </Fragment>
        )}
      </TableComponent>

      <NoDataFound noData={noData} />
    </TableContainer>
  );
};

export default DvConstraintTable;
